import { ReactNode, useCallback, useState } from "react";

import { UploadingFile } from "../../hooks/useS3Upload";
import Button from "../button/Button";
import Modal from "../modal/Modal";

/**
 * アップロード部分失敗の解決ダイアログ。
 *
 * `useS3Upload().uploadFiles` は失敗を throw せず `status: 'error'` の行として
 * 返すため、そのまま completed だけで続行すると失敗が闇に消える。
 * 本フックの `resolveUploadFailures(results)` を挟むと、失敗があるときに
 * 「再試行 / 失敗分を除いて続行 / キャンセル」の 3 択ダイアログを表示し、
 * ユーザーの選択を待ってから続行可否と対象ファイルを返す。
 *
 * 使い方:
 * ```tsx
 * const { uploadFiles, retryFiles, ... } = useS3Upload({ ... });
 * const { resolveUploadFailures, uploadFailureDialog } = useUploadFailureDialog({ retryFiles });
 *
 * const results = await uploadFiles(files);
 * const completed = await resolveUploadFailures(results);
 * if (completed === null) return; // キャンセル（中断。添付・本文は呼び出し側で保持）
 * // completed のみで続行
 *
 * return <>{...}{uploadFailureDialog}</>;
 * ```
 */

export interface UseUploadFailureDialogOptions {
  /** useS3Upload の retryFiles（error 行を再アップロード） */
  retryFiles: (_fileIds: string[]) => Promise<UploadingFile[]>;
}

export interface UseUploadFailureDialogReturn {
  /**
   * アップロード結果を確認し、失敗があればダイアログでユーザーに解決させる。
   * 戻り値: 続行してよい completed ファイル一覧（キャンセル時は null）。
   * 失敗がなければダイアログを出さず即座に completed を返す。
   */
  resolveUploadFailures: (
    _results: UploadingFile[],
  ) => Promise<UploadingFile[] | null>;
  /** 失敗ダイアログ要素（呼び出し側の JSX に常に含めておく） */
  uploadFailureDialog: ReactNode;
}

interface DialogState {
  failed: UploadingFile[];
  completed: UploadingFile[];
  resolve: (_value: UploadingFile[] | null) => void;
}

export const useUploadFailureDialog = ({
  retryFiles,
}: UseUploadFailureDialogOptions): UseUploadFailureDialogReturn => {
  const [dialogState, setDialogState] = useState<DialogState | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const resolveUploadFailures = useCallback(
    (results: UploadingFile[]): Promise<UploadingFile[] | null> => {
      const failed = results.filter((f) => f.status === "error");
      const completed = results.filter((f) => f.status === "completed");
      if (failed.length === 0) return Promise.resolve(completed);
      return new Promise((resolve) => {
        setDialogState({ failed, completed, resolve });
      });
    },
    [],
  );

  const handleRetry = useCallback(async () => {
    if (!dialogState) return;
    setIsRetrying(true);
    try {
      const retried = await retryFiles(dialogState.failed.map((f) => f.id));
      const stillFailed = retried.filter((f) => f.status === "error");
      const completed = [
        ...dialogState.completed,
        ...retried.filter((f) => f.status === "completed"),
      ];
      if (stillFailed.length === 0) {
        dialogState.resolve(completed);
        setDialogState(null);
      } else {
        // まだ失敗が残っている: ダイアログを開いたまま一覧を更新する
        setDialogState({ ...dialogState, failed: stillFailed, completed });
      }
    } finally {
      setIsRetrying(false);
    }
  }, [dialogState, retryFiles]);

  const handleContinue = useCallback(() => {
    if (!dialogState) return;
    dialogState.resolve(dialogState.completed);
    setDialogState(null);
  }, [dialogState]);

  const handleCancel = useCallback(() => {
    if (!dialogState || isRetrying) return;
    dialogState.resolve(null);
    setDialogState(null);
  }, [dialogState, isRetrying]);

  const uploadFailureDialog: ReactNode = dialogState ? (
    <Modal
      onClose={handleCancel}
      description={
        `${dialogState.failed.length}件のファイルをアップロードできませんでした。\n` +
        "通信環境をご確認のうえ再試行してください。"
      }
      footer={
        <>
          <Button
            size="small"
            theme="tertiary"
            variant="text"
            onClick={handleCancel}
            disabled={isRetrying}
          >
            キャンセル
          </Button>
          <div className="u-row u-gap8">
            {dialogState.completed.length > 0 && (
              <Button
                size="small"
                theme="secondary"
                variant="outlined"
                onClick={handleContinue}
                disabled={isRetrying}
              >
                失敗分を除いて続行
              </Button>
            )}
            <Button
              size="small"
              theme="primary"
              variant="filled"
              leftIcon="refresh"
              onClick={handleRetry}
              disabled={isRetrying}
              autoFocus
            >
              {isRetrying ? "再試行中..." : "再試行"}
            </Button>
          </div>
        </>
      }
    >
      <div className="u-col u-gap8">
        {dialogState.failed.map((f) => (
          <div key={f.id} className="u-col u-gap4">
            <div className="u-row u-gap8 u-align-center">
              <span
                className="material-symbols-rounded"
                style={{ fontSize: 18, color: "var(--soremo-green)" }}
              >
                error
              </span>
              <span className="bodytext-13" style={{ wordBreak: "break-all" }}>
                {f.file.name}
              </span>
            </div>
            {f.error && (
              <span
                className="label8"
                style={{ color: "var(--soremo-green)", paddingLeft: 26 }}
              >
                {f.error}
              </span>
            )}
          </div>
        ))}
      </div>
    </Modal>
  ) : null;

  return {
    resolveUploadFailures,
    uploadFailureDialog,
  };
};

export default useUploadFailureDialog;
